import { frameAutoLayoutMode, nodeConstraints, type SceneNode } from "@/document/model/nodes";
import { applyConstraintsToBounds } from "@/document/model/constraints";
import type { Rect } from "@/lib/math/rect";
import { transformRotationOrigin } from "@/lib/math/rotation";
import { getVectorNetworkBounds } from "./vectorGeometry";
import { contoursToPathData, pathDataToContours, transformContours } from "./pathGeometry";

/**
 * Resize/move a node (and its subtree) so its bounds become `bounds`, remapping
 * the underlying geometry the same way for every consumer.
 *
 * This is the single source of truth for turning a target bounding box into
 * concrete geometry. Both the commit path (`UpdateNodeBoundsCommand`) and the
 * live resize preview call it, which is what guarantees the preview renders
 * pixel-identically to the committed result (strokes included).
 */
export function applyBoundsToNode(node: SceneNode, bounds: Rect, options: { ignoreChildConstraints?: boolean } = {}): void {
  const fromBounds = node.bounds;
  if (node.type === "instance" && (node.bounds.width !== bounds.width || node.bounds.height !== bounds.height)) {
    node.explicitBounds = true;
  }
  if (node.type === "vectorNetwork") {
    transformVectorNetwork(node, node.bounds, bounds);
    transformNodeRotationOrigin(node, fromBounds, bounds);
    node.bounds = getVectorNetworkBounds(node.network);
    return;
  }

  if (node.type === "shape") {
    node.path = contoursToPathData(transformContours(pathDataToContours(node.path), node.bounds, bounds));
  }

  if (node.type === "boolean") {
    const transformed = transformContours(node.resolved.contours, node.bounds, bounds);
    node.resolved = {
      contours: transformed,
      path: contoursToPathData(transformed),
    };
    for (const source of node.sourceNodes) {
      applyBoundsToNode(source, transformBounds(source.bounds, node.bounds, bounds));
    }
  }

  if (node.type === "group" || node.type === "component" || node.type === "componentVariant" || node.type === "instance" || (node.type === "frame" && isPureMove(node.bounds, bounds))) {
    for (const child of node.children) {
      applyBoundsToNode(child, transformBounds(child.bounds, fromBounds, bounds));
    }
  }

  if (
    node.type === "frame" &&
    !isPureMove(node.bounds, bounds) &&
    frameAutoLayoutMode(node) === "off" &&
    !options.ignoreChildConstraints
  ) {
    for (const child of node.children) {
      applyBoundsToNode(
        child,
        applyConstraintsToBounds(child.bounds, fromBounds, bounds, nodeConstraints(child)),
      );
    }
  }

  transformNodeRotationOrigin(node, fromBounds, bounds);
  node.bounds = { ...bounds };
}

function transformNodeRotationOrigin(node: SceneNode, fromBounds: Rect, toBounds: Rect): void {
  if (node.transform.rotation === 0) {
    return;
  }

  const transformPoint = pointTransformer(fromBounds, toBounds);
  const nextOrigin = transformPoint(transformRotationOrigin(fromBounds, node.transform));
  node.transform = { ...node.transform, x: nextOrigin.x, y: nextOrigin.y };
}

function isPureMove(fromBounds: Rect, toBounds: Rect): boolean {
  return fromBounds.width === toBounds.width && fromBounds.height === toBounds.height;
}

function transformVectorNetwork(node: Extract<SceneNode, { type: "vectorNetwork" }>, fromBounds: Rect, toBounds: Rect): void {
  const transformPoint = pointTransformer(fromBounds, toBounds);

  for (const vertex of node.network.vertices) {
    const next = transformPoint(vertex);
    vertex.x = next.x;
    vertex.y = next.y;
  }

  for (const edge of node.network.edges) {
    if (edge.from.handle) {
      edge.from.handle = transformPoint(edge.from.handle);
    }

    if (edge.to.handle) {
      edge.to.handle = transformPoint(edge.to.handle);
    }
  }
}

export function transformBounds(bounds: Rect, fromGroup: Rect, toGroup: Rect): Rect {
  const transformPoint = pointTransformer(fromGroup, toGroup);
  const topLeft = transformPoint({ x: bounds.x, y: bounds.y });
  const bottomRight = transformPoint({ x: bounds.x + bounds.width, y: bounds.y + bounds.height });
  return {
    x: topLeft.x,
    y: topLeft.y,
    width: Math.max(0, bottomRight.x - topLeft.x),
    height: Math.max(0, bottomRight.y - topLeft.y),
  };
}

function pointTransformer(fromBounds: Rect, toBounds: Rect): (point: { x: number; y: number }) => { x: number; y: number } {
  const scaleX = fromBounds.width === 0 ? 1 : toBounds.width / fromBounds.width;
  const scaleY = fromBounds.height === 0 ? 1 : toBounds.height / fromBounds.height;
  return point => ({
    x: toBounds.x + (point.x - fromBounds.x) * scaleX,
    y: toBounds.y + (point.y - fromBounds.y) * scaleY,
  });
}
